// Import hooks.
import {
	useInputForm,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import services.
import {
	DateService
} from '@app/services';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import props.
import {
	InputDateWithoutInputTextBoxProps,
	InputFormProps,
} from '@app/props';

interface UseInputDateParameters {
	id: string;
	label: string;
	validationLabel?: string;
	infoText?:string;
	required: boolean;
	disabled: boolean;
	value?: Date;
	maxDate?: Date;
	minDate?: Date;
	disableOutOfRangeDates?: boolean;
	showValidation?: boolean;
}

type UseInputDateResults = UseInputFormResult<Date | undefined, InputDateWithoutInputTextBoxProps>

const useInputDate = (params: UseInputDateParameters): UseInputDateResults => {

	// #region exported methods

	/**
	 * Returns props for form input. 
	 **/
	const applyInputDateProps = (): InputDateWithoutInputTextBoxProps => {
		// First apply the form input props.
		let formProps: InputFormProps = applyProps();
		// Then apply the date props.
		return {
			...formProps,
			infoText: params.infoText,
			onChange: other.setInput,
			value: other.value,
			maxDate: params.maxDate,
			minDate: params.minDate,
			disableOutOfRangeDates: params.disableOutOfRangeDates,
		}
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Validates the current value.
	 * @param value
	 */
	const validateDate = (value: Date | undefined, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());
		let validationLabel: string = !String.isNullOrWhiteSpace(params.validationLabel) && params.validationLabel !== undefined ? params.validationLabel : 'date'
		if (required && value === undefined) {
			response = new ValidatorResponse(false, `The ${validationLabel} is required.`);
		}
		else if (value !== undefined) {
			if (value.isInvalidDate()) {
				return new ValidatorResponse(false, `The ${validationLabel} is invalid.`);
			}
			else {
				let minDateTime: number | undefined = params.minDate?.getTime() ?? undefined;
				let maxDateTime: number | undefined = params.maxDate?.getTime() ?? undefined;
				let valueTime: number = value.getTime();

				if (params.minDate !== undefined
					&& minDateTime !== undefined
					&& valueTime < minDateTime) {
					response = new ValidatorResponse(false, `The ${validationLabel} cannot be before ${DateService.getInstance().formatDate(params.minDate)}.`);
				}
				else if (params.maxDate !== undefined
					&& maxDateTime !== undefined
					&& valueTime > maxDateTime) {
					response = new ValidatorResponse(false, `The ${validationLabel} cannot be after ${DateService.getInstance().formatDate(params.maxDate)}.`);
				}
			}
		}

		return response;
	}

	// #endregion private methods


	// #region variables

	// Instantiate the 'date' input.
	const { applyProps, ...other } = useInputForm<Date | undefined>({
		id: params.id,
		label: params.label,
		required: params.required,
		disabled: params.disabled,
		initialInput: params.value,
		showValidation: params.showValidation,
		validators: [validateDate],
	});

	// #endregion variables


	return {
		applyProps: applyInputDateProps,
		...other,
	};
}

// Export parameters.
export {
	UseInputDateParameters,
}

// Export results.
export {
	UseInputDateResults,
}

export default useInputDate;